/**
 * CustomDashboardStore - MobX store for managing custom dashboards
 *
 * Features:
 * - Create and manage custom dashboard tiles
 * - Execute SQL-like queries against data
 * - Persist dashboard configurations to localStorage
 * - Export/import dashboard configurations
 */

import { makeAutoObservable, runInAction } from "mobx"
import { logger } from "../lib/logger"
import type { ParsedQuery, QueryResult } from "../lib/sqlQuery"
import { executeQuery, parseQuery } from "../lib/sqlQuery"

export type VisualizationType = "table" | "bar" | "line"

export interface DashboardTile {
  id: string
  title: string
  query: string
  parsedQuery: ParsedQuery
  visualizationType: VisualizationType
  queryResult?: QueryResult
  position: { x: number; y: number; w: number; h: number }
  error?: string
  lastUpdated?: number
}

export interface DashboardConfig {
  id: string
  name: string
  description?: string
  tiles: DashboardTile[]
  createdAt: number
  updatedAt: number
}

const STORAGE_KEY = "devpulse_custom_dashboards"
const ACTIVE_DASHBOARD_KEY = "devpulse_active_dashboard"

export class CustomDashboardStore {
  dashboards: Map<string, DashboardConfig> = new Map()
  activeDashboardId: string | null = null
  isLoading = false
  error: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * Initialize store by loading dashboards from localStorage
   */
  initialize(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      const activeDashboardId = localStorage.getItem(ACTIVE_DASHBOARD_KEY)

      if (stored) {
        const dashboardsArray: DashboardConfig[] = JSON.parse(stored)
        runInAction(() => {
          this.dashboards.clear()
          for (const dashboard of dashboardsArray) {
            this.dashboards.set(dashboard.id, dashboard)
          }
          this.activeDashboardId = activeDashboardId
        })
        logger.info("Loaded custom dashboards from localStorage", {
          component: "CustomDashboardStore",
          count: dashboardsArray.length,
        })
      }
    } catch (error) {
      logger.error("Failed to load custom dashboards from localStorage", {
        component: "CustomDashboardStore",
        error,
      })
      runInAction(() => {
        this.error = "Failed to load saved dashboards"
      })
    }
  }

  /**
   * Save dashboards to localStorage
   */
  private persist(): void {
    try {
      const dashboardsArray = Array.from(this.dashboards.values())
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dashboardsArray))
      if (this.activeDashboardId) {
        localStorage.setItem(ACTIVE_DASHBOARD_KEY, this.activeDashboardId)
      } else {
        localStorage.removeItem(ACTIVE_DASHBOARD_KEY)
      }
      logger.debug("Saved custom dashboards to localStorage", {
        component: "CustomDashboardStore",
        count: dashboardsArray.length,
      })
    } catch (error) {
      logger.error("Failed to save custom dashboards to localStorage", {
        component: "CustomDashboardStore",
        error,
      })
    }
  }

  /**
   * Create new dashboard
   */
  createDashboard(name: string, description?: string): string {
    const id = `dashboard-${Date.now()}`
    const dashboard: DashboardConfig = {
      id,
      name,
      description,
      tiles: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }

    this.dashboards.set(id, dashboard)
    this.activeDashboardId = id
    this.persist()

    logger.info("Created new dashboard", {
      component: "CustomDashboardStore",
      dashboardId: id,
      name,
    })

    return id
  }

  /**
   * Delete dashboard
   */
  deleteDashboard(dashboardId: string): void {
    this.dashboards.delete(dashboardId)
    if (this.activeDashboardId === dashboardId) {
      this.activeDashboardId =
        this.dashboards.size > 0 ? Array.from(this.dashboards.keys())[0] : null
    }
    this.persist()

    logger.info("Deleted dashboard", {
      component: "CustomDashboardStore",
      dashboardId,
    })
  }

  /**
   * Set active dashboard
   */
  setActiveDashboard(dashboardId: string): void {
    if (this.dashboards.has(dashboardId)) {
      this.activeDashboardId = dashboardId
      this.persist()
    }
  }

  /**
   * Add tile to active dashboard
   */
  addTile(title: string, query: string, visualizationType: VisualizationType): string | null {
    if (!this.activeDashboardId) {
      logger.error("No active dashboard", { component: "CustomDashboardStore" })
      return null
    }

    const dashboard = this.dashboards.get(this.activeDashboardId)
    if (!dashboard) return null

    try {
      const parsedQuery = parseQuery(query)
      const tileId = `tile-${Date.now()}`

      // Calculate position for new tile (simple grid layout)
      const tilesCount = dashboard.tiles.length
      const col = tilesCount % 2
      const row = Math.floor(tilesCount / 2)

      const tile: DashboardTile = {
        id: tileId,
        title,
        query,
        parsedQuery,
        visualizationType,
        position: { x: col * 6, y: row * 4, w: 6, h: 4 },
        lastUpdated: Date.now(),
      }

      dashboard.tiles.push(tile)
      dashboard.updatedAt = Date.now()
      this.persist()

      logger.info("Added tile to dashboard", {
        component: "CustomDashboardStore",
        dashboardId: this.activeDashboardId,
        tileId,
      })

      return tileId
    } catch (error) {
      logger.error("Failed to add tile", {
        component: "CustomDashboardStore",
        error,
        query,
      })
      return null
    }
  }

  /**
   * Update tile configuration
   */
  updateTile(
    tileId: string,
    updates: Partial<Pick<DashboardTile, "title" | "query" | "visualizationType">>,
  ): void {
    if (!this.activeDashboardId) return

    const dashboard = this.dashboards.get(this.activeDashboardId)
    if (!dashboard) return

    const tile = dashboard.tiles.find((t) => t.id === tileId)
    if (!tile) return

    if (updates.title) tile.title = updates.title
    if (updates.visualizationType) tile.visualizationType = updates.visualizationType

    if (updates.query && updates.query !== tile.query) {
      try {
        tile.query = updates.query
        tile.parsedQuery = parseQuery(updates.query)
        tile.error = undefined
        tile.queryResult = undefined // Clear old results
      } catch (error) {
        tile.error = error instanceof Error ? error.message : "Invalid query"
        logger.error("Failed to update tile query", {
          component: "CustomDashboardStore",
          error,
          tileId,
        })
      }
    }

    tile.lastUpdated = Date.now()
    dashboard.updatedAt = Date.now()
    this.persist()
  }

  /**
   * Delete tile from active dashboard
   */
  deleteTile(tileId: string): void {
    if (!this.activeDashboardId) return

    const dashboard = this.dashboards.get(this.activeDashboardId)
    if (!dashboard) return

    const index = dashboard.tiles.findIndex((t) => t.id === tileId)
    if (index !== -1) {
      dashboard.tiles.splice(index, 1)
      dashboard.updatedAt = Date.now()
      this.persist()

      logger.info("Deleted tile", {
        component: "CustomDashboardStore",
        dashboardId: this.activeDashboardId,
        tileId,
      })
    }
  }

  /**
   * Update tile positions (for drag-and-drop)
   */
  updateTilePositions(
    positions: Array<{ id: string; position: { x: number; y: number; w: number; h: number } }>,
  ): void {
    if (!this.activeDashboardId) return

    const dashboard = this.dashboards.get(this.activeDashboardId)
    if (!dashboard) return

    for (const { id, position } of positions) {
      const tile = dashboard.tiles.find((t) => t.id === id)
      if (tile) {
        tile.position = position
      }
    }

    dashboard.updatedAt = Date.now()
    this.persist()
  }

  /**
   * Reorder tiles (for drag-and-drop)
   */
  reorderTiles(tileIds: string[]): void {
    if (!this.activeDashboardId) return

    const dashboard = this.dashboards.get(this.activeDashboardId)
    if (!dashboard) return

    // Create a map for quick lookup
    const tileMap = new Map(dashboard.tiles.map((tile) => [tile.id, tile]))

    // Reorder tiles based on the new order
    const reorderedTiles = tileIds
      .map((id) => tileMap.get(id))
      .filter((tile): tile is DashboardTile => {
        if (!tile) {
          logger.warn("Tile not found during reorder, skipping", {
            component: "CustomDashboardStore",
            dashboardId: this.activeDashboardId,
          })
          return false
        }
        return true
      })

    dashboard.tiles = reorderedTiles
    dashboard.updatedAt = Date.now()
    this.persist()

    logger.info("Reordered tiles", {
      component: "CustomDashboardStore",
      dashboardId: this.activeDashboardId,
      count: tileIds.length,
    })
  }

  /**
   * Execute query for a tile
   */
  executeTileQuery(tileId: string, data: Record<string, unknown>[]): void {
    if (!this.activeDashboardId) return

    const dashboard = this.dashboards.get(this.activeDashboardId)
    if (!dashboard) return

    const tile = dashboard.tiles.find((t) => t.id === tileId)
    if (!tile) return

    try {
      const result = executeQuery(tile.parsedQuery, data)
      runInAction(() => {
        tile.queryResult = result
        tile.error = undefined
        tile.lastUpdated = Date.now()
      })

      logger.debug("Executed tile query", {
        component: "CustomDashboardStore",
        tileId,
        rowCount: result.rows.length,
      })
    } catch (error) {
      runInAction(() => {
        tile.error = error instanceof Error ? error.message : "Query execution failed"
        tile.queryResult = undefined
      })

      logger.error("Failed to execute tile query", {
        component: "CustomDashboardStore",
        error,
        tileId,
        query: tile.query,
      })
    }
  }

  /**
   * Export dashboard configuration as JSON
   */
  exportDashboard(dashboardId: string): string | null {
    const dashboard = this.dashboards.get(dashboardId)
    if (!dashboard) return null

    return JSON.stringify(dashboard, null, 2)
  }

  /**
   * Import dashboard configuration from JSON
   */
  importDashboard(jsonConfig: string): string | null {
    try {
      const config = JSON.parse(jsonConfig) as DashboardConfig

      // Validate basic structure
      if (!config.name || !Array.isArray(config.tiles)) {
        throw new Error("Invalid dashboard configuration")
      }

      // Generate new ID to avoid conflicts
      const newId = `dashboard-${Date.now()}`
      const dashboard: DashboardConfig = {
        ...config,
        id: newId,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }

      // Validate and re-parse queries
      for (const tile of dashboard.tiles) {
        try {
          tile.parsedQuery = parseQuery(tile.query)
          tile.queryResult = undefined // Clear old results
          tile.error = undefined
        } catch (error) {
          tile.error = `Invalid query: ${error instanceof Error ? error.message : "Unknown error"}`
        }
      }

      this.dashboards.set(newId, dashboard)
      this.activeDashboardId = newId
      this.persist()

      logger.info("Imported dashboard", {
        component: "CustomDashboardStore",
        dashboardId: newId,
        name: dashboard.name,
        tilesCount: dashboard.tiles.length,
      })

      return newId
    } catch (error) {
      logger.error("Failed to import dashboard", {
        component: "CustomDashboardStore",
        error,
      })
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Import failed"
      })
      return null
    }
  }

  /**
   * Get active dashboard
   */
  get activeDashboard(): DashboardConfig | null {
    return this.activeDashboardId ? this.dashboards.get(this.activeDashboardId) || null : null
  }

  /**
   * Get all dashboards as array
   */
  get dashboardsList(): DashboardConfig[] {
    return Array.from(this.dashboards.values()).sort((a, b) => b.updatedAt - a.updatedAt)
  }
}
